import React, { useState, useEffect } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { FiArrowLeft, FiRefreshCw, FiUsers, FiTrash2 } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const StuckMiningUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const limit = 100;

  const fetchStuckUsers = async (page = currentPage, isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/stuck-mining-users?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      let data;
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch stuck mining users");
      }

      if (data.status) {
        setUsers(data.data?.users || []);
        setTotal(data.data?.total || 0);
        if (isRefresh) {
          toast.success("List refreshed");
        }
      } else {
        throw new Error(data.message || "Failed to fetch stuck mining users");
      }
    } catch (err) {
      setError(err.message || "Failed to load stuck users");
      if (isRefresh) toast.error(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStuckUsers(currentPage);
  }, [currentPage]);

  const totalPages = Math.ceil(total / limit) || 1;

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleClearAll = async () => {
    if (!window.confirm("Are you sure you want to clear all stuck mining sessions?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/reset-all-stuck-mining`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      if (res.ok && data.status) {
        toast.success(data.message || "Successfully reset stuck mining sessions");
        fetchStuckUsers(currentPage, true);
      } else {
        toast.error(data.message || "Failed to clear stuck mining sessions");
      }
    } catch (err) {
      toast.error(err.message || "Failed to clear stuck mining sessions");
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-5 sm:p-8 space-y-6 w-full">
          {/* Header */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link to="/user" className="hover:text-black hover:underline transition">Users</Link>
              <span>/</span>
              <span className="text-gray-900 font-bold bg-gray-200 px-2 py-0.5 rounded">Stuck Mining Users</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">Stuck Mining Users</h1>
                  <p className="text-xs text-gray-500 mt-0.5">Users whose mining sessions have expired but are still stuck.</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchStuckUsers(currentPage, true)}
                  disabled={refreshing || loading}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-sm"
                >
                  <FiRefreshCw size={13} className={refreshing ? "animate-spin text-green-600" : ""} />
                  <span>Refresh</span>
                </button>
                <button
                  onClick={handleClearAll}
                  disabled={users.length === 0}
                  className="flex items-center gap-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 border border-red-600 px-3.5 py-2 rounded-xl transition shadow-sm"
                >
                  <FiTrash2 size={13} />
                  <span>Clear All Stuck Users</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <FiUsers size={16} className="text-gray-500" />
                <span>Stuck Users ({total})</span>
              </h3>
              <p className="text-xs text-gray-400">
                Page {currentPage} of {totalPages}
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16 text-sm text-gray-400">Loading...</div>
            ) : error ? (
              <div className="p-8 text-center text-red-600 text-sm bg-red-50">{error}</div>
            ) : users.length === 0 ? (
              <div className="py-24 text-center text-gray-400">
                <FiUsers size={42} className="mx-auto text-gray-300 mb-3" />
                <p className="text-base font-semibold text-gray-700">No Stuck Users Found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wider">
                      <th className="px-5 py-3.5">ID</th>
                      <th className="px-5 py-3.5">Name</th>
                      <th className="px-5 py-3.5">Email</th>
                      <th className="px-5 py-3.5">Mining Start</th>
                      <th className="px-5 py-3.5">Mining End</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-4 text-gray-500 font-mono">#{user.id}</td>
                        <td className="px-5 py-4 font-bold text-gray-800">{user.username || "—"}</td>
                        <td className="px-5 py-4 text-gray-600">{user.email}</td>
                        <td className="px-5 py-4 text-gray-500">{new Date(user.startDateAndTime).toLocaleString()}</td>
                        <td className="px-5 py-4 text-gray-500">{new Date(user.endDateAndTime).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && !error && users.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total} users
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="px-3.5 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="px-3.5 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StuckMiningUsers;
